const ABSOLUTE_URL_REGEX = /^https?:\/\//i;

const rawBase =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE)) ||
  (typeof process !== "undefined" &&
    (process.env?.VITE_API_BASE_URL || process.env?.VITE_API_BASE)) ||
  "";

const API_BASE = String(rawBase || "").trim().replace(/\/+$/, "");

export function getApiBase() {
  return API_BASE;
}

export function buildApiUrl(path = "") {
  const base = getApiBase();
  if (typeof path !== "string") {
    throw new TypeError("buildApiUrl expects a string path");
  }
  if (!path) {
    return base;
  }
  if (ABSOLUTE_URL_REGEX.test(path)) {
    return path;
  }
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (!base) {
    return normalizedPath;
  }
  return `${base}${normalizedPath}`;
}

export class HttpError extends Error {
  constructor(message, { status, url, body } = {}) {
    super(message);
    this.name = "HttpError";
    this.status = typeof status === "number" ? status : null;
    this.url = url || null;
    this.body = body;
  }
}

const RETRYABLE_STATUS = new Set([502, 503, 504, 522, 524]);
const DEFAULT_ATTEMPTS = 4;
const DEFAULT_DELAY_MS = 900;
const WAKE_THROTTLE_MS = 15000;

let wakeInFlight = null;
let lastWakeAt = 0;

function isRetryableNetworkError(error) {
  if (!error) return false;
  if (error.name === "TypeError") return true;
  const message = typeof error.message === "string" ? error.message : "";
  if (!message) return false;
  return /network/i.test(message) || /failed to fetch/i.test(message);
}

function delay(ms, signal) {
  if (!ms) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(onDone, ms);

    function cleanup() {
      clearTimeout(timeout);
      if (signal) {
        signal.removeEventListener("abort", onAbort);
      }
    }

    function onDone() {
      cleanup();
      resolve();
    }

    function onAbort() {
      cleanup();
      const abortError = typeof DOMException !== "undefined"
        ? new DOMException("Aborted", "AbortError")
        : Object.assign(new Error("Aborted"), { name: "AbortError" });
      reject(abortError);
    }

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener("abort", onAbort);
    }
  });
}

async function wakeApiBase(base) {
  if (!base || !ABSOLUTE_URL_REGEX.test(base)) return;
  const now = Date.now();
  if (wakeInFlight) {
    return wakeInFlight;
  }
  if (now - lastWakeAt < WAKE_THROTTLE_MS) {
    return;
  }
  const url = `${base}/api/health`;
  wakeInFlight = (async () => {
    try {
      await fetch(url, { cache: "no-store", mode: "cors" }).catch(() => null);
    } finally {
      lastWakeAt = Date.now();
      wakeInFlight = null;
    }
  })();
  return wakeInFlight;
}

function computeDelay(baseDelay, attempt) {
  const delayMs = Math.max(0, Number(baseDelay) || 0);
  if (!delayMs) return 0;
  return delayMs * Math.pow(2, attempt - 1);
}

function toUrl(input) {
  if (typeof input !== "string") {
    throw new TypeError("fetchJsonWithRetry expects a string URL");
  }
  if (ABSOLUTE_URL_REGEX.test(input)) {
    return input;
  }
  return buildApiUrl(input);
}

export async function fetchJsonWithRetry(
  pathOrUrl,
  options = {},
  { attempts = DEFAULT_ATTEMPTS, retryDelayMs = DEFAULT_DELAY_MS, warmup = true } = {}
) {
  const url = toUrl(pathOrUrl);
  const fetchOptions = { ...options };
  const signal = fetchOptions.signal;
  const base = getApiBase();
  const shouldWarm = warmup && Boolean(base) && ABSOLUTE_URL_REGEX.test(base);

  let lastError = null;

  for (let attempt = 1; attempt <= Math.max(1, attempts); attempt += 1) {
    if (signal?.aborted) {
      const abortError = typeof DOMException !== "undefined"
        ? new DOMException("Aborted", "AbortError")
        : Object.assign(new Error("Aborted"), { name: "AbortError" });
      throw abortError;
    }

    try {
      const response = await fetch(url, fetchOptions);
      const text = await response.text();

      if (!response.ok) {
        const error = new HttpError(`Request failed with status ${response.status}`, {
          status: response.status,
          url: response.url,
          body: text,
        });

        if (RETRYABLE_STATUS.has(response.status) && attempt < attempts) {
          lastError = error;
          if (shouldWarm) {
            try {
              await wakeApiBase(base);
            } catch {}
          }
          await delay(computeDelay(retryDelayMs, attempt), signal).catch((abortError) => {
            throw abortError;
          });
          continue;
        }

        throw error;
      }

      if (!text) {
        return null;
      }

      try {
        return JSON.parse(text);
      } catch (parseError) {
        parseError.message = `Failed to parse JSON from ${response.url}: ${parseError.message}`;
        parseError.url = response.url;
        parseError.body = text;
        throw parseError;
      }
    } catch (error) {
      if (error?.name === "AbortError") {
        throw error;
      }

      if (error instanceof HttpError) {
        if (RETRYABLE_STATUS.has(error.status) && attempt < attempts) {
          lastError = error;
          if (shouldWarm) {
            try {
              await wakeApiBase(base);
            } catch {}
          }
          await delay(computeDelay(retryDelayMs, attempt), signal).catch((abortError) => {
            throw abortError;
          });
          continue;
        }
        throw error;
      }

      if (error instanceof SyntaxError) {
        throw error;
      }

      if (!isRetryableNetworkError(error) || attempt >= attempts) {
        throw error;
      }

      lastError = error;
      if (shouldWarm) {
        try {
          await wakeApiBase(base);
        } catch {}
      }
      await delay(computeDelay(retryDelayMs, attempt), signal).catch((abortError) => {
        throw abortError;
      });
    }
  }

  if (lastError) {
    throw lastError;
  }
  throw new Error("Request failed");
}
